import { ReactNode, useMemo } from 'react';

import classNames from 'classnames';
import { tv } from 'tailwind-variants';

import { NumberWithCurrency, NumberWithPercentage } from '@anyx/shared-ui';
import { CurrencyUtils, NumberUtils } from '@anyx/shared-utils';


export type StatRecord =
  | {
      currentStat: number;
      previousStat: number;
    }
  | null
  | undefined;

export interface TrendStatisticItemProps {
  title: string;
  statistic?: StatRecord;
  format?: 'ratio' | 'number' | 'currency';
  key: string;
}

const TrendDashboardItem = ({
  title,
  loading,
  skeleton,
  children,
}: {
  title: string;
  loading?: boolean;
  skeleton?: boolean;
  children: ReactNode;
}) => {
  const displayNode = useMemo(() => {
    if (loading) {
      return (
        <>
          <div className="text-skeleton mt-2 animate-pulse" />
          <div className="text-skeleton mt-4 w-full animate-pulse" />
        </>
      );
    }

    if (skeleton) {
      return <div>-</div>;
    }

    return children;
  }, [children, loading, skeleton]);

  return (
    <div className="flex justify-between gap-x-2 border-b last:border-0 odd:border-r nth-last-[-n+2]:odd:border-b-0 sm:border-r md:nth-last-[-n+4]:border-b-0 xl:justify-start xl:gap-x-1 xl:border-b-0 xl:pl-3 xl:odd:border-r-0">
      <div className="text-gray-9 -order-1 flex flex-col xl:order-1">
        <span className="xl:text-2xs text-secondary text-sm">{title}</span>
        {displayNode}
      </div>
    </div>
  );
};

const TrendDashboardStatisticItem = ({
  title,
  statistic,
  format = 'number',
}: TrendStatisticItemProps) => {
  const isStatisticNull = statistic === null;
  const { currentStat = 0, previousStat = 0 } = statistic || {};
  const trend = currentStat / previousStat - 1;
  const isInvalid = trend === Infinity || Number.isNaN(trend);
  const isPositive = trend > 0;
  const isNegative = trend < 0;
  const isNeutral = trend === 0;

  const trendInPercentage = `${NumberUtils.format(Math.abs(trend) * 100, {
    maximumFractionDigits: 2,
  })}%`;

  const currentStatFormatted = useMemo(() => {
    if (format === 'ratio') {
      return <NumberWithPercentage amount={currentStat * 100} />;
    }

    if (format === 'currency') {
      return (
        <NumberWithCurrency
          amount={currentStat}
          currency={CurrencyUtils.getCurrentCurrency()}
          currencyClassName="text-4xs lg:text-2xs"
        />
      );
    }

    return NumberUtils.format(currentStat);
  }, [currentStat, format]);

  return (
    <TrendDashboardItem title={title} loading={statistic === undefined} skeleton={isStatisticNull}>
      <span className="text-lg font-semibold xl:text-xl">{currentStatFormatted}</span>
      <span
        className={classNames('text-3xs font-semibold xl:font-normal', {
          'text-success': isPositive && !isInvalid,
          'text-failure': isNegative && !isInvalid,
        })}
      >
        {isInvalid ? (
          '-'
        ) : (
          <>
            {isPositive && `+${trendInPercentage}`}
            {isNegative && `-${trendInPercentage}`}
            {isNeutral && `${trendInPercentage}`}
          </>
        )}
      </span>
    </TrendDashboardItem>
  );
};

const dashboardStyle = tv({
  base: 'grid grid-cols-2 border-y bg-transparent *:bg-white *:px-1 *:py-4 *:pr-2 *:pl-4 md:grid-cols-4 last:md:border-r xl:max-h-max xl:min-h-24 xl:auto-cols-fr xl:grid-flow-col xl:grid-cols-none xl:divide-x xl:border-t-0 xl:bg-white xl:px-2 xl:py-4 *:xl:py-0',
});

export const TrendDashboard = ({
  children,
  className,
}: {
  className?: string;
  children: ReactNode;
}) => <div className={dashboardStyle({ className })}>{children}</div>;

TrendDashboard.Item = TrendDashboardItem;
TrendDashboard.StatisticItem = TrendDashboardStatisticItem;
